import { useCallback, useRef, useState } from 'react';
import { Database, Download, Eye, FileText, Paperclip, Upload, X } from 'lucide-react';

import { PdfPreviewDialog } from '@/components/PdfPreviewDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { KbSyncBadge, useSyncSource, useSourceStatus } from '@/features/knowledge_base';
import { triggerBlobDownload } from '@/features/finance/staff-invoices/api';
import { fetchProjectAttachmentBlob } from '../api';
import {
  useDeleteProjectAttachment,
  useProjectAttachments,
  useUploadProjectAttachment,
} from '../queries';
import type { ProjectAttachment } from '../types';

function humanSize(bytes: number): string {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

const isPdf = (name: string) => /\.pdf$/i.test(name);

/**
 * Files linked to a project (ADR-078). Backed by the dedicated `ProjectAttachment`
 * model — readable by any staff member with access to the project (Employees
 * included), unlike the documents library. Drag-drop upload + inline PDF preview.
 */
export function ProjectAttachmentsPanel({
  projectId,
  kbSyncEnabled,
}: {
  projectId: number;
  /** The project's KB sync switch — off hides every KB control (DEV13-113). */
  kbSyncEnabled: boolean;
}) {
  const query = useProjectAttachments(projectId);
  const upload = useUploadProjectAttachment(projectId);
  const remove = useDeleteProjectAttachment(projectId);
  const syncToRag = useSyncSource();

  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<ProjectAttachment | null>(null);

  const previewId = preview?.public_id ?? null;
  const fetchPreviewBlob = useCallback(
    () => fetchProjectAttachmentBlob(previewId as string),
    [previewId],
  );

  const files = query.data?.results ?? [];
  const syncStatus = useSourceStatus(
    'projects.projectattachment',
    files.map((f) => f.id),
    kbSyncEnabled,
  );

  async function handleFiles(list: FileList | null) {
    if (!list) return;
    for (const file of Array.from(list)) {
      await upload.mutateAsync(file);
    }
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Paperclip className="size-4" /> Attachments
        </CardTitle>
        <CardDescription>Files linked to this project.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <button
          type="button"
          disabled={upload.isPending}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={cn(
            'flex w-full flex-col items-center gap-1 rounded-md border border-dashed p-6 text-sm transition-colors',
            dragging ? 'border-primary bg-accent/40' : 'border-input hover:bg-accent/30',
            upload.isPending && 'cursor-not-allowed opacity-60',
          )}
        >
          <Upload className="text-muted-foreground size-5" />
          <span className="text-muted-foreground">
            {upload.isPending ? 'Uploading…' : 'Drag a file here, or click to upload'}
          </span>
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />

        {query.isError ? (
          <p className="text-destructive text-sm">Failed to load attachments.</p>
        ) : query.isPending ? (
          <div className="space-y-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : files.length === 0 ? (
          <p className="text-muted-foreground text-sm">No attachments yet.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {files.map((f) => {
              const kbStatus = syncStatus.data?.get(f.id);
              return (
              <li key={f.id} className="flex items-center gap-2 p-2 text-sm">
                <FileText className="text-muted-foreground size-4 shrink-0" aria-hidden />
                <span className="min-w-0 flex-1 truncate" title={f.name}>
                  {f.name}
                </span>
                {f.size ? <span className="text-muted-foreground shrink-0 text-xs">{humanSize(f.size)}</span> : null}
                {kbSyncEnabled ? <KbSyncBadge status={kbStatus} className="shrink-0" /> : null}
                {isPdf(f.name) ? (
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Preview ${f.name}`}
                    onClick={() => setPreview(f)}
                  >
                    <Eye className="size-4" />
                  </Button>
                ) : null}
                {kbSyncEnabled ? (
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`${kbStatus?.synced ? 'Re-sync' : 'Sync'} ${f.name} to Knowledge Base`}
                    title={kbStatus?.synced ? 'Re-sync to Knowledge Base' : 'Sync to Knowledge Base'}
                    disabled={syncToRag.isPending}
                    onClick={() =>
                      syncToRag.mutate({ entity: 'projects.projectattachment', entity_id: f.id })
                    }
                  >
                    <Database className={cn('size-4', kbStatus?.synced && 'text-emerald-600')} />
                  </Button>
                ) : null}
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Download ${f.name}`}
                  onClick={() =>
                    void fetchProjectAttachmentBlob(f.public_id).then((b) => triggerBlobDownload(b, f.name))
                  }
                >
                  <Download className="size-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Remove ${f.name}`}
                  onClick={() => remove.mutate(f.public_id)}
                  disabled={remove.isPending}
                >
                  <X className="size-4" />
                </Button>
              </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      <PdfPreviewDialog
        open={Boolean(preview)}
        onOpenChange={(o) => !o && setPreview(null)}
        title={preview?.name ?? ''}
        fetchBlob={previewId ? fetchPreviewBlob : undefined}
      />
    </Card>
  );
}
